import React from 'react';
import svgPaths from "../imports/svg-m71wgcy0pd";
import clsx from "clsx";
import { ProjectCardMenu } from "../../../components/shared/ProjectCardMenu";
import { useDeleteAssetMutation } from "../../../features/assets/assetsApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import toast from 'react-hot-toast';


export interface UploadedFile {
  id: string;
  name: string;
  type: 'Image' | 'Video' | 'Vector';
  size: string;
  src?: string;
}



function Wrapper({ children, src }: React.PropsWithChildren<{ src?: string }>) {
  return (
    <div className="bg-[#f0f2f5] aspect-[272/178] overflow-clip relative rounded-[6px] shrink-0 w-full group">
      <div className="absolute h-full left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] w-full" data-name="image 14">
        {src ? (
          <video
            className="absolute max-w-none object-cover size-full"
            controlsList="nodownload"
            playsInline
            muted
            preload="metadata"
            src={src}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-[#e4e7ec] w-full h-full flex items-center justify-center">
              <svg className="w-12 h-12 text-[#98a2b3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

function Wrapper1({ children, additionalClassNames = "" }: React.PropsWithChildren<{ additionalClassNames?: string }>) {
  return (
    <div className={additionalClassNames}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        {children}
      </svg>
    </div>
  );
}

function Frame({ children }: React.PropsWithChildren) {
  return (
    <Wrapper1 additionalClassNames="relative shrink-0 size-[16px]">
      <g id="Frame">{children}</g>
    </Wrapper1>
  );
}

function Helper() {
  return (
    <div className="relative shrink-0 size-[4px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 4 4">
        <circle cx="2" cy="2" fill="var(--fill-0, #98A2B3)" id="Ellipse 19" r="2" />
      </svg>
    </div>
  );
}

type Helper1Props = {
  text: string;
  text1: string;
};

function Helper1({ text, text1 }: Helper1Props) {
  return (
    <div className="content-stretch flex gap-[4px] items-center relative shrink-0">
      <p className="font-sans font-normal relative shrink-0 text-[#475367] text-[13px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        {text}
      </p>
      <Helper />
      <p className="font-sans font-normal relative shrink-0 text-[#475367] text-[13px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        {text1}
      </p>
    </div>
  );
}

interface UploadedFilesViewProps {
  files: UploadedFile[];
  onUpload: () => void;
}

export function UploadedFilesView({ files, onUpload }: UploadedFilesViewProps) {
  const [deleteAsset, { isLoading: isDeleting }] = useDeleteAssetMutation();
  const [assetToDelete, setAssetToDelete] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState('All Files');

  const handleDelete = async (id: string) => {
    try {
      await deleteAsset(id).unwrap();
      toast.success('File deleted successfully');
    } catch (err) {
      console.error('Failed to delete asset:', err);
      toast.error('Failed to delete file');
    } finally {
      setAssetToDelete(null);
    }
  };

  const filteredFiles = React.useMemo(() => {
    if (activeTab === 'All Files') return files;
    return files.filter(file => {
      if (activeTab === 'Images') return file.type === 'Image';
      if (activeTab === 'Videos') return file.type === 'Video';
      if (activeTab === 'Vectors') return file.type === 'Vector';
      return false;
    });
  }, [files, activeTab]);

  return (
    <div className="content-stretch flex flex-col gap-[32px] items-start w-full">
      <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
        <div className="bg-white h-[48px] relative rounded-[12px] shrink-0 w-[448px]" data-name="Search bar">
          <div className="content-stretch flex items-center overflow-clip px-[12px] py-[3px] relative rounded-[inherit] size-full">
            <div className="content-stretch flex items-center relative shrink-0 w-full">
              <div className="content-stretch flex gap-[8px] items-center relative flex-1">
                <div className="relative shrink-0 size-[16px]" data-name="search-normal">
                  <Wrapper1 additionalClassNames="absolute contents inset-0">
                    <g id="search-normal">
                      <path d={svgPaths.p20103e00} id="Vector" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                      <path d={svgPaths.p133f8470} id="Vector_2" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                      <g id="Vector_3" opacity="0"></g>
                    </g>
                  </Wrapper1>
                </div>
                <input
                  type="text"
                  placeholder="Search files..."
                  className="font-sans font-medium leading-[24px] relative flex-1 text-[#101928] text-[13px] outline-none placeholder-[#98a2b3] px-2"
                  style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
                />
              </div>
            </div>
          </div>
          <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[12px]" />
        </div>
        <div
          className="bg-[#30a84b] content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 cursor-pointer hover:bg-[#288e3e] transition-colors"
          data-name="Primary - Sm"
          onClick={onUpload}
        >
          <div className="content-stretch flex gap-[4px] items-center relative shrink-0">
            <Frame>
              <path d="M8 2V10" id="Vector" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              <path d={svgPaths.p26e09a00} id="Vector_2" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              <path d={svgPaths.p23ad1400} id="Vector_3" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
            </Frame>
            <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
              Upload Files
            </p>
          </div>
        </div>
      </div>
      <div className="content-stretch flex flex-col gap-[24px] items-start relative shrink-0 w-full">
        <div className="content-stretch flex gap-[32px] items-center relative shrink-0 overflow-x-auto w-full pb-2">
          {['All Files', 'Images', 'Videos', 'Vectors'].map((tab) => (
            <div 
              key={tab} 
              className="content-stretch flex flex-col gap-[0px] items-start relative shrink-0 cursor-pointer group"
              onClick={() => setActiveTab(tab)}
            >
              <p className={clsx(
                "font-sans font-normal relative shrink-0 text-[13px] text-center text-nowrap transition-colors",
                activeTab === tab ? "text-[#227735]" : "text-[#475367] group-hover:text-[#227735]"
              )} style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                {tab}
              </p>
              <div className={clsx(
                "h-[2px] rounded-tl-[6px] rounded-tr-[6px] shrink-0 w-full transition-all",
                activeTab === tab ? "bg-[#227735] opacity-100" : "bg-[#475367] opacity-0 group-hover:opacity-50"
              )} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[16px] w-full">
          {filteredFiles.map((file) => (
            <div key={file.id} className="content-stretch flex flex-col gap-[14px] items-start relative shrink-0 w-full group overflow-hidden">
              {file.type === 'Video' ? (
                <div className="relative w-full">
                  <Wrapper src={file.src} />
                </div>
              ) : (
                <div className="bg-[#f0f2f5] aspect-[272/178] overflow-clip relative rounded-[6px] shrink-0 w-full group">
                  <div className="absolute left-1/2 size-full top-1/2 translate-x-[-50%] translate-y-[-50%]" data-name="image 14">
                    {file.src && <img alt="" className="size-full object-contain" src={file.src} />}
                  </div>
                </div>
              )}

              <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 flex-1 min-w-0">
                  <p className="font-sans font-medium relative shrink-0 text-[#101928] text-[14px] w-full truncate" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                    {file.name}
                  </p>
                  <Helper1 text={file.type} text1={file.size} />
                </div>
                <ProjectCardMenu 
                  onDelete={() => setAssetToDelete(file.id)}
                  onSaveToCms={() => console.log('Save to CMS:', file.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity z-20"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={!!assetToDelete} onOpenChange={(open) => !open && setAssetToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-sans">Are you sure you want to delete this file?</AlertDialogTitle>
            <AlertDialogDescription className="font-sans">
              This action cannot be undone. This will permanently delete your file from our servers (this will also remove it from any templates using it).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-sans">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => assetToDelete && handleDelete(assetToDelete)}
              className="bg-red-600 hover:bg-red-700 text-white font-sans"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

